import React, { useState, useEffect, useCallback } from "react";
import {
  Route,
  useLocation,
  useNavigate,
  createBrowserRouter,
  RouterProvider,
  createRoutesFromElements,
  Outlet,
} from "react-router-dom";
import HomePage from "./pages/HomePage";
import CreatePage from "./pages/CreatePage";
import ChatPage from "./pages/ChatPage";
import ProfilePage from "./pages/ProfilePage";
import WelcomePage from "./pages/WelcomePage";
import LoginPage from "./pages/LoginPage";
import SignUpPage from "./pages/SignUpPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import OnboardingPage from "./pages/OnboardingPage";
import BottomNavigation from "./components/BottomNavigation";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ActivityProvider } from "./contexts/ActivityContext";
import ActivityChatPage from "./pages/ActivityChatPage";
import NotificationsPage from "./pages/NotificationsPage";
import SettingsPage from "./pages/SettingsPage";
import MapPage from "./pages/MapPage";
import ForYouPage from "./pages/ForYouPage";
import ProfileBuilderPage from "./pages/ProfileBuilderPage";
import MatchPage from "./pages/MatchPage";
import ManualCreatePage from "./pages/ManualCreatePage";
import { ToastProvider } from "./contexts/ToastContext";
import ToastContainer from "./components/ToastContainer";
import SwipeIndicator from "./components/SwipeIndicator";
import EditProfilePage from "./pages/EditProfilePage";
import ActivityPage from "./pages/ActivityPage";
import PrivacySecurityPage from "./pages/PrivacySecurityPage";
import SupportPage from "./pages/SupportPage";
import EmailVerificationPage from "./pages/EmailVerificationPage";
import { AndroidViewportFix } from "./utils/androidViewportFix";
import { NotificationManager } from "./utils/notificationManager";
import { WebPushNotificationManager } from "./utils/webPushNotificationManager";
import {
  useSwipeNavigation,
  SwipeNavigationManager,
} from "./utils/swipeNavigation";
import { KeyboardHandler } from "./utils/keyboardHandler";
import { Capacitor } from "@capacitor/core";
import { motion } from "framer-motion";
import { WifiOff, RefreshCcw } from "lucide-react";
// import TestPage from './pages/TestPage';
import { useVersionCheck } from "./hooks/useVersionCheck";
import VersionBlockModal from "./components/VersionBlockModal";
import SplashScreen from "./components/SplashScreen";
import AccountLinkingBanner from "./components/AccountLinkingBanner";
import { crashlytics } from "./services/firebase";
import { clearExpiredImageCaches } from "./utils/getCachedImage";
import { App as CapacitorApp } from "@capacitor/app";
import NotFoundPage from "./components/NotFoundPage";
import {
  startBackgroundLocationUpdates,
  stopBackgroundLocationUpdates,
} from "./services/backgroundLocationService";

const App: React.FC = () => {
  // Automatically clear expired profile image caches on app startup
  useEffect(() => {
    clearExpiredImageCaches();
  }, []);

  // Define the router using the new Data Router API
  // This allows hooks like useBlocker to work inside children
  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route path="/" element={<AppContent />}>
        <Route path="welcome" element={<WelcomePage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="signup" element={<SignUpPage />} />
        <Route path="forgot-password" element={<ForgotPasswordPage />} />
        <Route path="verify-email" element={<EmailVerificationPage />} />

        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          <Route path="onboarding" element={<OnboardingPage />} />
          <Route path="profile-builder" element={<ProfileBuilderPage />} />
          <Route index element={<HomePage />} />{" "}
          {/* Changed path="/" to index */}
          <Route path="foryou" element={<ForYouPage />} />
          <Route path="create" element={<CreatePage />} />
          <Route path="create/manual" element={<ManualCreatePage />} />
          <Route path="chat" element={<ChatPage />} />
          <Route path="chat/:chatId" element={<ActivityChatPage />} />
          <Route path="activity/:activityId" element={<ActivityPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="profile/:userId" element={<ProfilePage />} />
          <Route path="profile/edit" element={<EditProfilePage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="privacy-security" element={<PrivacySecurityPage />} />
          <Route path="support" element={<SupportPage />} />
          <Route path="map" element={<MapPage />} />
          <Route path="match/:activityId" element={<MatchPage />} />
        </Route>
        {/* Catch-all route for 404 */}
        <Route
          path="*"
          element={
            <React.Suspense fallback={<div>Loading...</div>}>
              <NotFoundPage />
            </React.Suspense>
          }
        />
      </Route>,
    ),
  );

  return (
    <AuthProvider>
      <ToastProvider>
        <ActivityProvider>
          <AppWrapper router={router} />
        </ActivityProvider>
      </ToastProvider>
    </AuthProvider>
  );
};

// Wrapper component that has access to auth context
const AppWrapper: React.FC<{ router: any }> = ({ router }) => {
  const { currentUser, isLoading } = useAuth();
  const [showSplash, setShowSplash] = useState(true);
  const [authTimeout, setAuthTimeout] = useState(false);

  // Auth timeout after 30 seconds
  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => {
        setAuthTimeout(true);
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  // App is ready when auth check is complete
  const appReady = !isLoading;

  // Show timeout error
  if (authTimeout && isLoading) {
    return (
      <div className="fixed inset-0 bg-white z-[9999] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Connection Issue
          </h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            We're having trouble connecting to our servers. This might be a
            temporary issue.
          </p>
          <p className="text-gray-600 mb-6">
            Please try again later or contact our support team if the problem
            persists.
          </p>
          <a
            href="mailto:support@whoisin.sg"
            className="inline-block bg-primary text-white px-6 py-3 rounded-full font-semibold hover:bg-primary/90 transition-colors"
          >
            Contact Support
          </a>
          <button
            onClick={() => window.location.reload()}
            className="block w-full mt-4 text-primary font-medium hover:underline"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {showSplash && (
        <SplashScreen
          isReady={appReady}
          onComplete={() => setShowSplash(false)}
        />
      )}
      <RouterProvider router={router} />
    </>
  );
};

const OfflineNotice: React.FC = () => {
  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <div className="fixed inset-0 bg-white z-[100] flex flex-col items-center justify-center p-6 text-center">
      {/* Soft background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary/5 rounded-full blur-3xl opacity-50" />

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative z-10"
      >
        {/* Icon with animated pulse */}
        <div className="relative mb-8 flex justify-center">
          <div className="absolute inset-0 bg-red-100 rounded-full scale-150 animate-pulse opacity-20" />
          <div className="bg-red-50 p-6 rounded-full">
            <WifiOff className="w-12 h-12 text-red-500" />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-3">
          Connection Lost
        </h1>

        <p className="text-gray-500 mb-10 max-w-[280px] mx-auto leading-relaxed">
          It looks like you're offline. Please check your internet connection to
          continue using{" "}
          <span className="text-primary font-semibold">Who's in?</span>
        </p>

        <div className="space-y-4 w-full max-w-xs mx-auto">
          <button
            onClick={handleRetry}
            className="w-full bg-primary text-white py-4 rounded-2xl font-bold shadow-lg shadow-primary/25 flex items-center justify-center gap-3 active:scale-95 transition-transform"
          >
            <RefreshCcw className="w-5 h-5" />
            Try Again
          </button>

          <p className="text-xs text-gray-400">
            The app will automatically reconnect once you're back online.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

const AppContent: React.FC = () => {
  const {
    isAuthenticated,
    currentUser,
    shouldShowPrivateRelayBanner,
    dismissPrivateRelayBanner,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Version check
  const { isAllowed, message, isChecking, config, recheckVersion } =
    useVersionCheck(currentUser?.id);

  // Keyboard state for hiding bottom navigation
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  // Swipe indicator state
  const [swipeState, setSwipeState] = useState({
    isVisible: false,
    direction: "left" as "left" | "right",
    progress: 0,
  });

  useEffect(() => {
    if (!currentUser) return;
    const enabled = currentUser.notificationSettings?.backgroundLocationEnabled;
    if (enabled) {
      startBackgroundLocationUpdates(currentUser.id);
    } else {
      stopBackgroundLocationUpdates();
    }
  }, [
    currentUser?.id,
    currentUser?.notificationSettings?.backgroundLocationEnabled,
  ]);

  // Initialize swipe navigation for mobile devices
  const { addSwipeListeners, isMobile } = useSwipeNavigation({
    enableSwipeBack: true,
    enableSwipeForward: false, // Disabled for now as it can be confusing
    swipeThreshold: 80, // Require reasonable swipe distance
    velocityThreshold: 0.4, // Moderate velocity requirement
    excludeRoutes: ["/welcome"],
    onSwipeStart: () => {
      setSwipeState((prev) => ({ ...prev, isVisible: true }));
    },
    onSwipeProgress: (progress, direction) => {
      setSwipeState({ isVisible: true, direction, progress });
    },
    onSwipeEnd: () => {
      setSwipeState((prev) => ({ ...prev, isVisible: false, progress: 0 }));
    },
  });

  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Track navigation history for better swipe experience
  useEffect(() => {
    SwipeNavigationManager.pushRoute(location.pathname);
  }, [location.pathname]);

  // Set up swipe listeners
  useEffect(() => {
    if (isMobile && isAuthenticated) {
      return addSwipeListeners();
    }
  }, [addSwipeListeners, isMobile, isAuthenticated]);

  // Listen for notification navigation events
  useEffect(() => {
    const handleNotificationNavigation = (event: CustomEvent) => {
      const { path } = event.detail;
      navigate(path);
    };

    window.addEventListener(
      "notification-navigate",
      handleNotificationNavigation as EventListener,
    );

    return () => {
      window.removeEventListener(
        "notification-navigate",
        handleNotificationNavigation as EventListener,
      );
    };
  }, [navigate]);

  // Handle deep links (URLs that open the app)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const setupDeepLinking = async () => {
      // Handle app opened from URL
      CapacitorApp.addListener("appUrlOpen", (data: any) => {

        // Parse the URL to extract the path
        // Handles both web URLs (https://app.whoisin.sg/activity/123)
        // and custom scheme URLs (whosin://activity/123)
        let path = "";

        if (data.url.includes("://")) {
          // For custom scheme URLs (whosin://activity/123)
          if (data.url.startsWith("whosin://")) {
            // Extract path after whosin://
            path = data.url.replace("whosin://", "");
            // Ensure it starts with /
            if (!path.startsWith("/")) {
              path = "/" + path;
            }
          } else {
            // For web URLs - parse properly
            try {
              const url = new URL(data.url);
              // For web URLs - use pathname (BrowserRouter)
              if (url.protocol === "https:" || url.protocol === "http:") {
                path = url.pathname;
                // If there's still a hash (for backward compatibility), use it
                if (url.hash) {
                  path = url.hash.replace("#", "");
                }
              }
            } catch (e) {
              crashlytics.logError(
                `Failed to parse deep link URL: ${data?.url}. Error: ${e?.message}`,
                "App.tsx",
                e,
              );
            }
          }
        }

        if (path) {
          navigate(path);
        }
      });
    };

    setupDeepLinking();
  }, [navigate]);

  // Initialize mobile features now that Firebase authentication works
  useEffect(() => {
    const initializeMobile = async () => {
      try {
        if (!Capacitor || !Capacitor.isNativePlatform()) {
          // Web platform - initialize web push notifications
          if (currentUser?.id) {
            const webPushEnabled = await WebPushNotificationManager.initialize(
              currentUser.id,
            );
            if (webPushEnabled) {
            } else {
            }
          }
          return;
        }

        // Apply Android viewport fixes for older devices
        AndroidViewportFix.apply();

        // Initialize keyboard handler
        await KeyboardHandler.getInstance().initialize();

        // DISABLED: Apply responsive UI scaling based on screen aspect ratio
        // ResponsiveUIManager.apply(); // Completely disabled to ensure standard UI for all devices

        // Avoid SystemBars visibility/style mutations on Android because they can
        // race with IME inset visibility and cancel keyboard show requests.

        // Initialize notifications (mobile)
        const notificationResults = await NotificationManager.initialize(
          currentUser?.id,
        );
        if (notificationResults.local || notificationResults.push) {
          await NotificationManager.setupNotificationHandlers(currentUser?.id);
        }

        // Explicitly associate FCM token with user after login
        if (currentUser?.id) {
          const { PushNotificationManager } =
            await import("./utils/pushNotificationManager");
          await PushNotificationManager.associateTokenWithUser(currentUser.id);
        }
      } catch (error) {
        crashlytics.logError(
          `Mobile initialization failed. Error: ${JSON.stringify(error?.message)}`,
          "App.tsx",
          error,
        );
      }
    };

    // Initialize immediately instead of waiting 1 second
    initializeMobile();
  }, [currentUser?.id]);

  // Handle keyboard visibility for hiding bottom navigation
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const cb = (isOpen: boolean, height: number) => {
      setIsKeyboardVisible(isOpen);
    };
    KeyboardHandler.getInstance().initialize();
    KeyboardHandler.getInstance().onKeyboardChange(cb);
    return () => {
      KeyboardHandler.getInstance().offKeyboardChange(cb);
    };
  }, []);

  const hideBottomNavRoutes = [
    "/welcome",
    "/login",
    "/signup",
    "/forgot-password",
    "/onboarding",
    "/profile-builder",
    "/match/",
  ];

  const shouldHideBottomNav = hideBottomNavRoutes.some((path) =>
    path.endsWith("/")
      ? location.pathname.startsWith(path)
      : location.pathname === path,
  );

  // Hide bottom nav when keyboard is visible OR when on specific routes
  const showBottomNav =
    isAuthenticated &&
    currentUser?.hasCompletedOnboarding &&
    !shouldHideBottomNav &&
    !isKeyboardVisible;


  // Show version block modal if version is not allowed (and not checking)
  if (!isChecking && !isAllowed) {
    // Determine if the block is due to version being disabled
    const isVersionDisabled = config?.isVersionDisabled === true;
    return (
      <VersionBlockModal
        message={message}
        onRetry={recheckVersion}
        isVersionDisabled={isVersionDisabled}
      />
    );
  }

  return isOffline ? (
    <OfflineNotice />
  ) : (
    <div className="w-full bg-white font-sans h-screen relative flex flex-col">
      {shouldShowPrivateRelayBanner && (
        <AccountLinkingBanner onDismiss={dismissPrivateRelayBanner} />
      )}
      <ToastContainer />
      <SwipeIndicator
        isVisible={swipeState.isVisible}
        direction={swipeState.direction}
        progress={swipeState.progress}
      />
      <main
        className="flex-1 w-full flex flex-col min-h-0 bg-purple-500"
        style={{
          paddingTop: "env(safe-area-inset-top, 0px)",
          paddingBottom: isKeyboardVisible
            ? "0px"
            : "env(safe-area-inset-bottom, 0px)",
        }}
      >
        <div
          id="app-scroll-container"
          className={`flex-1 overflow-y-auto hide-scrollbar min-h-0 bg-white
            ${showBottomNav ? "pb-16" : ""}`} // nav has height of h-16 - this is required for bottom nav spacing for the page
          style={{
            WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
          }}
        >
          <Outlet />
        </div>
        {showBottomNav && <BottomNavigation />}
      </main>
    </div>
  );
};

export default App;
